import type { Duration } from 'luxon'

import type { PostAttributes, UserAttributes, UserRoleAttributes } from '@/server/models'

import type { UserRoleAssignedSource } from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'
import type { UserAuthRole } from '@/client/services/userRole/types'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const UserRoleQ = {
  ...makeQueryModelV2<UserRoleAttributes>('user_role')([
    'id',
    'user_id',
    'role',
    'assigner_user_id',
    'assigned_source',
    'remover_user_id',
    'removed_source',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: UserRoleQ.user_id,
    },
    Assigner: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: UserRoleQ.assigner_user_id,
    },
    Remover: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: UserRoleQ.remover_user_id,
    },
  }),

  userHasRole: ({
    userId,
    role,
  }: {
    userId: number
    role: UserAuthRole
  }): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${UserRoleQ.where({ userId, role })})
  `,

  where: (
    {
      id,
      userId,
      role,
      assignedSource = null,
      groupBy = null,
      include = [],
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      role?: UserAuthRole | null
      assignedSource?: UserRoleAssignedSource | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      include?: IncludeAssociations
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<PostAttributes> => SQL`
        FROM ${UserRoleQ}
        ${includeAssociationsToSql(UserRoleQ, include)}
        WHERE
            ${id != null ? SQL`${UserRoleQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${UserRoleQ.user_id} = ${userId} AND` : SQL``}
            ${role != null ? SQL`${UserRoleQ.role} = ${role} AND` : SQL``}
            ${assignedSource != null ? SQL`${UserRoleQ.assigned_source} = ${assignedSource} AND` : SQL``}
            ${paranoid ? SQL`${UserRoleQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getAssigner: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: UserRoleQ.assigner_user_id,
    }),

  getRemover: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: UserRoleQ.remover_user_id,
    }),

  getUsersToDeactivate: ({
    lookaheadWindow,
    customWhere,
  }: {
    lookaheadWindow?: Duration
    customWhere?: SQLFragment<unknown>
  }): SQLFragment<UserRoleAttributes[]> =>
    UserRoleQ.where(
      {
        role: 'user:zoom:activated',
        include: [
          {
            association: 'User',
            required: true,
          },
        ],
      },
      SQL`
      NOT (${UserQ.isHostingRecentOrFutureSessions({ lookaheadWindow })})
      AND ${customWhere != null ? customWhere : SQL`TRUE`}
    `
    ),
}

export default UserRoleQ
